import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { HandCoins, Loader2, MapPin } from "lucide-react";
import { adminGet, money } from "./utils";
import { toast } from "sonner";

// A numbered pin (1, 2, 3…) so dispatch can see the shortest-route order
const numberedIcon = (n) =>
  new L.DivIcon({
    html: `<div style="background:#d97706;color:#fff;padding:4px 0;width:32px;height:32px;border-radius:9999px;font-size:13px;font-weight:800;box-shadow:0 2px 8px rgba(0,0,0,0.3);text-align:center;line-height:24px;border:2px solid white;">${n}</div>`,
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

const originIcon = new L.DivIcon({
  html: '<div style="background:#0d9488;color:#fff;padding:4px 8px;border-radius:9999px;font-size:11px;font-weight:700;box-shadow:0 2px 8px rgba(0,0,0,0.3);border:2px solid white;">START</div>',
  className: "",
  iconSize: [56, 24],
  iconAnchor: [28, 12],
});

export function CashRouteMap({ rides, auth }) {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load the nearest-neighbor ordered route from the backend. We refresh
  // whenever the underlying ride list changes so dispatch sees fresh data.
  useEffect(() => {
    let cancelled = false;
    if (!auth) return;
    (async () => {
      setLoading(true);
      try {
        const data = await adminGet(auth, "rides/cash-pickups/route");
        if (!cancelled) setRoute(data);
      } catch (err) {
        if (!cancelled) toast.error(err.message || "Could not load optimized route");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, (rides || []).length]);

  const withCoords = route?.route || [];
  const origin = route?.origin;

  const center = useMemo(() => {
    if (origin) return [origin.lat, origin.lng];
    if (withCoords.length === 0) return null;
    return [
      withCoords.reduce((s, r) => s + r.pickup_lat, 0) / withCoords.length,
      withCoords.reduce((s, r) => s + r.pickup_lng, 0) / withCoords.length,
    ];
  }, [origin, withCoords]);

  if (loading && !route) {
    return (
      <div className="bg-white border border-brand-navy/10 rounded-2xl p-6 text-center text-sm text-brand-muted flex items-center justify-center gap-2" data-testid="cash-route-map-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Optimizing cash sweep…
      </div>
    );
  }

  if (withCoords.length === 0) {
    return (
      <div className="bg-white border border-brand-navy/10 rounded-2xl p-6 text-center text-sm text-brand-muted" data-testid="cash-route-map-empty">
        No cash pickups to plot yet — pickup addresses are being geocoded.
      </div>
    );
  }

  // Polyline path: origin → stop1 → stop2 → … so the driver sees the exact loop
  const polyPositions = [];
  if (origin) polyPositions.push([origin.lat, origin.lng]);
  withCoords.forEach((r) => polyPositions.push([r.pickup_lat, r.pickup_lng]));

  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden" data-testid="cash-route-map">
      <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2 flex-wrap">
        <HandCoins className="w-4 h-4 text-amber-700" />
        <div className="text-sm font-semibold text-amber-900">
          Cash sweep · {withCoords.length} pickup{withCoords.length === 1 ? "" : "s"}
          {route?.total_miles ? (
            <span className="ml-2 text-amber-800 font-normal" data-testid="cash-route-total-miles">
              · ~{route.total_miles} mi total
            </span>
          ) : null}
        </div>
        <div className="ml-auto text-[11px] text-amber-800/80 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Numbered in nearest-neighbor order
        </div>
      </div>
      <div style={{ height: 340 }}>
        <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://osm.org">OpenStreetMap</a>'
          />
          {origin && (
            <Marker position={[origin.lat, origin.lng]} icon={originIcon}>
              <Popup>Sweep starts here</Popup>
            </Marker>
          )}
          {polyPositions.length >= 2 && (
            <Polyline
              positions={polyPositions}
              pathOptions={{ color: "#d97706", weight: 4, opacity: 0.75, dashArray: "8,8" }}
            />
          )}
          {withCoords.map((r) => (
            <Marker
              key={r.id}
              position={[r.pickup_lat, r.pickup_lng]}
              icon={numberedIcon(r.seq)}
              data-testid={`cash-pin-${r.seq}`}
            >
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold">#{r.seq} · {r.member_name}</div>
                  <div className="text-xs text-slate-600">{r.trip_date} · {r.trip_time}</div>
                  <div className="text-xs text-slate-600 mt-1">📍 {r.pickup}</div>
                  <div className="text-xs text-slate-600">→ {r.dropoff}</div>
                  <div className="mt-2 text-xs">
                    <span className={`inline-block px-2 py-0.5 rounded-full font-semibold ${
                      r.preferred_payment_method === "cash" ? "bg-amber-100 text-amber-900" :
                      "bg-blue-100 text-blue-900"
                    }`}>
                      {r.preferred_payment_method?.toUpperCase()} · {money(r.hold_amount_cents)}
                    </span>
                    {typeof r.leg_miles === "number" && (
                      <span className="ml-2 text-[11px] text-slate-600">{r.leg_miles} mi from prev</span>
                    )}
                  </div>
                  {r.driver_name && <div className="text-xs text-emerald-700 mt-1">Driver: {r.driver_name}</div>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
